import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TextInput from '../../components/TextInput';
import FileUpload from '../../components/FileUpload'; // Changed from FileInput to FileUpload
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import clientApi from '../../api/clientApi';
import { useToast } from '../../contexts/ToastContext';
import { ROUTES } from '../../constants';

const EditClient = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [generalError, setGeneralError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    logo: undefined,
  });
  const [currentLogoUrl, setCurrentLogoUrl] = useState(null);
  const [clientFound, setClientFound] = useState(true);

  useEffect(() => {
    const fetchClient = async () => {
      setLoading(true);
      setGeneralError(null);
      setValidationErrors({});
      try {
        const response = await clientApi.getClientByIdentifier(id);
        if (response.success && response.data) {
          const clientToEdit = response.data;
          setFormData({
            name: clientToEdit.name,
            logo: undefined,
          });
          setCurrentLogoUrl(clientToEdit.logo);
          setClientFound(true);
        } else {
          setClientFound(false);
          setGeneralError(response.message || 'Client not found.');
        }
      } catch (err) {
        console.error('Failed to fetch client:', err);
        setClientFound(false);
        setGeneralError(err.response?.data?.message || 'Failed to load client. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchClient();
  }, [id, addToast]);

  const handleChange = (e) => {
    const { name, value, type, files, checked } = e.target; // Destructure 'checked' here
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : (type === 'file' ? files[0] : value),
    });
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleLogoRemove = () => {
    setFormData({ ...formData, logo: null });
    setCurrentLogoUrl(null);
    if (validationErrors.logo) {
      setValidationErrors(prev => ({ ...prev, logo: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneralError(null);
    setValidationErrors({});

    const data = new FormData();
    data.append('name', formData.name);

    if (formData.logo instanceof File) {
      data.append('logo', formData.logo);
    } else if (formData.logo === null) {
      data.append('logo', '');
    }

    data.append('_method', 'PUT');

    try {
      await clientApi.updateClient(id, data);
      addToast('Client updated successfully!', 'success');
      navigate(ROUTES.CLIENTS);
    } catch (err) {
      console.error('Failed to update client:', err);
      if (err.response && err.response.status === 422) {
        setValidationErrors(err.response.data.errors || {});
        addToast(err.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else {
        addToast(err.response?.data?.message || 'Failed to update client. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <SkeletonLoader type="form" />
      </div>
    );
  }

  if (!clientFound) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <h1 className="text-3xl font-bold mb-4">Client Not Found</h1>
        <p className="text-text-gray">{generalError || 'The client you are trying to edit does not exist.'}</p>
        <Button
          onClick={() => navigate(ROUTES.CLIENTS)}
          className="mt-4 bg-gray-500 hover:bg-gray-600 text-white"
        >
          Go to Clients List
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white">
      <h1 className="text-3xl font-bold mb-6">Edit Client</h1>
      {generalError && <div className="text-red-500 mb-4">{generalError}</div>}
      <form onSubmit={handleSubmit}>
        <TextInput
          label="Client Name"
          name="name"
          id="name"
          value={formData.name}
          onChange={handleChange}
          required={true}
          placeholder="Client Name"
          error={validationErrors?.name?.[0]}
        />
        <FileUpload // Changed to FileUpload
          label="Client Logo"
          name="logo"
          id="logo"
          onChange={handleChange}
          error={validationErrors?.logo?.[0]}
          currentFileUrl={currentLogoUrl}
          onRemoveFile={handleLogoRemove}
          fileType="image"
          fileName="Logo"
        />
        <div className="flex items-center justify-between mt-6">
          <Button
            type="submit"
            className="bg-accent-red hover:bg-red-700 text-white"
            loading={loading}
          >
            Update Client
          </Button>
          <Button
            type="button"
            onClick={() => navigate(ROUTES.CLIENTS)}
            className="bg-gray-500 hover:bg-gray-600 text-white"
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditClient;